import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuction } from './hooks/useAuctions';
import { images, certificationServices } from '../../utils/api';
import { ChevronLeftIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import LotFormModal from './components/LotFormModal';
import LotBasicInfo from './components/LotBasicInfo';
import LotDetails from './components/LotDetails';
import LotPopulationInfo from './components/LotPopulationInfo';
import LotPriceInfo from './components/LotPriceInfo';
import LotVarietyInfo from './components/LotVarietyInfo';
import LotNotes from './components/LotNotes';
import ImageViewerModal from '../../components/shared/ImageViewerModal';

export default function AuctionLotDetailsPage() {
  const { auctionId, lotNumber } = useParams();
  const navigate = useNavigate();
  const { data: auction, isLoading } = useAuction(auctionId);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [services, setServices] = useState({});

  const handleImageClick = (images, index) => {
    setSelectedImage({ images, index, coin: lot });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleNavigate = (newIndex) => {
    setSelectedImage(prev => ({ ...prev, index: newIndex }));
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await certificationServices.getAll();
        const servicesMap = {};
        servicesData.forEach(service => {
          servicesMap[service.id] = service.name;
        });
        setServices(servicesMap);
      } catch (error) {
        console.error('Failed to fetch certification services:', error);
      }
    };
    fetchServices();
  }, []);

  const lot = useMemo(() => {
    return auction?.lots?.find(l => l.lot_number === lotNumber);
  }, [auction, lotNumber]);

  const sortedLots = useMemo(() => {
    if (!auction?.lots) return [];
    return [...auction.lots].sort((a, b) => parseInt(a.lot_number) - parseInt(b.lot_number));
  }, [auction?.lots]);

  const currentIndex = useMemo(() => {
    if (!lot) return -1;
    return sortedLots.findIndex(l => l.lot_number === lotNumber);
  }, [lot, sortedLots, lotNumber]);

  const isFirstLot = currentIndex === 0;
  const isLastLot = currentIndex === sortedLots.length - 1;

  useEffect(() => {
    if (!isLoading && auction && !lot) {
      navigate(`/auctions/${auctionId}`, { replace: true });
    }
  }, [isLoading, auction, lot, navigate, auctionId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-red-600">Auction not found</div>
        </div>
      </div>
    );
  }

  if (!lot) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Lot {lot.lot_number}
              </h2>
              <p className="text-gray-600 hidden sm:block">
                {auction.auction_house_name} <span className="mx-2">•</span> {auction.name}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                if (currentIndex > 0) {
                  navigate(`/auctions/${auctionId}/lots/${sortedLots[currentIndex - 1].lot_number}`);
                }
              }}
              disabled={isFirstLot}
              className={`inline-flex items-center px-3 py-1.5 border rounded-md shadow-sm text-sm font-medium ${
                !isFirstLot
                  ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline ml-2">Previous</span>
            </button>
            <button
              onClick={() => {
                if (currentIndex < sortedLots.length - 1) {
                  navigate(`/auctions/${auctionId}/lots/${sortedLots[currentIndex + 1].lot_number}`);
                }
              }}
              disabled={isLastLot}
              className={`inline-flex items-center px-3 py-1.5 border rounded-md shadow-sm text-sm font-medium ${
                !isLastLot
                  ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
              }`}
            >
               <span className="hidden sm:inline mr-2">Next</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate(`/auctions/${auctionId}`)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Back
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              title="Edit coin"
              className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Basic Info, Images, and Details Grid */}
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="rounded-t-lg">
            <LotBasicInfo lot={lot} services={services} />
          </div>
          
          {/* Images */}
          <div className="px-4 py-2 sm:px-6 bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {lot.images?.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map((image, index) => (
                <div 
                  key={image.id} 
                  className="relative bg-gray-100 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="aspect-w-1 aspect-h-1 w-full h-48 overflow-hidden rounded-lg bg-white">
                    <img
                      src={images.getImage(image.file_path, image.source)}
                      alt={`Lot ${lot.lot_number} image ${index + 1}`}
                      className="w-full h-full object-contain p-2 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleImageClick(lot.images, index)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Details Grid */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <LotDetails lot={lot} />
              <LotVarietyInfo lot={lot} />
              <LotPopulationInfo lot={lot} />
              <LotPriceInfo lot={lot} auction={auction} />
            </div>
            {lot.notes && (
              <div className="mt-6">
                <LotNotes lot={lot} />
              </div>
            )}
          </div>
        </div>

        {selectedImage && (
          <ImageViewerModal
            selectedImage={selectedImage}
            onClose={closeImageModal}
            onNavigate={handleNavigate}
          />
        )}
      </div>

      <LotFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        lot={lot}
        auctionId={auctionId}
      />
    </div>
  );
}
